import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, KeyRound, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';

/**
 * PinDialog - Reusable dialog for PIN operations (setup, verify, sign, unlock)
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close callback
 * @param {function} onVerified - Called with the pin when verified successfully
 * @param {'setup'|'verify'|'sign'|'unlock'} mode - Operation mode
 * @param {string} title - Custom title
 * @param {string} description - Custom description
 */
export default function PinDialog({ open, onClose, onVerified, mode = 'verify', title, description }) {
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // For setup: 1=enter, 2=confirm
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (open) {
      setPin(['', '', '', '']);
      setConfirmPin(['', '', '', '']);
      setStep(1);
      setTimeout(() => inputRefs[0]?.current?.focus(), 100);
    }
  }, [open]);

  const handleChange = (index, value, isConfirm = false) => {
    if (!/^\d?$/.test(value)) return;
    
    const arr = isConfirm ? [...confirmPin] : [...pin];
    arr[index] = value;
    isConfirm ? setConfirmPin(arr) : setPin(arr);

    // Auto-focus next input
    if (value && index < 3) {
      const refs = isConfirm ? confirmRefs : inputRefs;
      refs[index + 1]?.current?.focus();
    }
  };

  const handleKeyDown = (index, e, isConfirm = false) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const refs = isConfirm ? confirmRefs : inputRefs;
      refs[index - 1]?.current?.focus();
    }
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const pinStr = pin.join('');
    if (pinStr.length !== 4) {
      toast.error('Ingrese los 4 dígitos del PIN');
      return;
    }

    if (mode === 'setup') {
      if (step === 1) {
        setStep(2);
        setTimeout(() => confirmRefs[0]?.current?.focus(), 100);
        return;
      }
      const confirmStr = confirmPin.join('');
      if (pinStr !== confirmStr) {
        toast.error('Los PINs no coinciden');
        setConfirmPin(['', '', '', '']);
        setTimeout(() => confirmRefs[0]?.current?.focus(), 100);
        return;
      }

      setLoading(true);
      try {
        await authAPI.pinSetup(pinStr);
        toast.success('PIN configurado correctamente');
        onVerified?.(pinStr);
        onClose();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Error al configurar PIN');
      } finally {
        setLoading(false);
      }
      return;
    }

    // verify/sign/unlock mode - just pass the pin back
    onVerified?.(pinStr);
  };

  const getConfig = () => {
    switch (mode) {
      case 'setup':
        return {
          icon: <KeyRound className="w-6 h-6 text-emerald-500" />,
          title: title || 'Configurar PIN de Firma',
          description: description || 'Cree un PIN de 4 dígitos para firmar documentos médicos. Este PIN es personal e intransferible.',
          buttonLabel: step === 1 ? 'Siguiente' : 'Configurar PIN',
        };
      case 'sign':
        return {
          icon: <ShieldCheck className="w-6 h-6 text-brand-navy" />,
          title: title || 'Firmar Documento',
          description: description || 'Ingrese su PIN de 4 dígitos para firmar este documento. Una vez firmado, quedará bloqueado.',
          buttonLabel: 'Firmar',
        };
      case 'unlock':
        return {
          icon: <Lock className="w-6 h-6 text-brand-deep" />,
          title: title || 'Desbloquear Documento',
          description: description || 'Ingrese su PIN para desbloquear este documento firmado y permitir su edición.',
          buttonLabel: 'Desbloquear',
        };
      default:
        return {
          icon: <Lock className="w-6 h-6 text-gray-500" />,
          title: title || 'Verificar PIN',
          description: description || 'Ingrese su PIN de 4 dígitos.',
          buttonLabel: 'Verificar',
        };
    }
  };

  const config = getConfig();

  const renderPinInputs = (values, refs, isConfirm = false) => (
    <div className="flex gap-3 justify-center">
      {values.map((digit, i) => (
        <Input
          key={i}
          ref={refs[i]}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value, isConfirm)}
          onKeyDown={(e) => handleKeyDown(i, e, isConfirm)}
          className="w-14 h-14 text-center text-2xl font-bold tracking-widest border-2 focus:border-emerald-500 focus:ring-emerald-500"
          data-testid={`pin-input-${isConfirm ? 'confirm-' : ''}${i}`}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            {config.icon}
            {config.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {mode === 'setup' && step === 2 && (
            <Label className="text-center block text-sm font-medium text-gray-600">
              Confirme su PIN
            </Label>
          )}
          {mode === 'setup' && step === 1 && (
            <Label className="text-center block text-sm font-medium text-gray-600">
              Ingrese su nuevo PIN
            </Label>
          )}
          
          {(mode !== 'setup' || step === 1) && renderPinInputs(pin, inputRefs)}
          {mode === 'setup' && step === 2 && renderPinInputs(confirmPin, confirmRefs, true)}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pin-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (mode !== 'setup' ? pin.join('').length !== 4 : step === 2 ? confirmPin.join('').length !== 4 : pin.join('').length !== 4)}
            data-testid="pin-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {config.buttonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
